import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Card, EmptyState, Loading, ModuleHero, Notice, StatCard, StatusPill, Tabs } from '../components/ui';
import { BTN_SOFT, BTN_SOFT_STYLE } from '../lib/ui';
import { BAND, COMPANY_STATUS, FEATURE, VERDICT, companyDisplayName, flagEmoji, formatDateTime, formatPct, formatTokens, formatUsd, pctTone } from '../lib/format';
import type { Band, CompanyRow, Overview } from '../types';
import CompanyLogo from '../components/CompanyLogo';

const BAND_ORDER: Band[] = ['BAD', 'AVERAGE', 'GOOD'];
const TH = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-[#c084a0]';
const TD = 'px-3 py-2.5 align-top';

export default function AiMonitor() {
  const { data, loading, error, reload } = useApi<Overview>('/support/ai/overview', 15000);

  if (loading && !data) return <Loading label="Loading AI metrics…" />;
  if (error && !data) return <Notice tone="rose">{error}</Notice>;
  if (!data) return null;

  const { totals, accuracy, failures } = data;
  const failureCount = failures.aiErrors.length + failures.stuckDocs.length;

  return (
    <div className="space-y-6">
      <ModuleHero
        eyebrow="AI monitor"
        title="AI accuracy, cost & failures"
        description="Every AI-platform call in the rating pipeline — document verification on upload, rating drafts and the analyst copilot — measured against what the analyst finally decided. Prompt versions have their own ledger."
        actions={
          <>
            <Link to="/smith" className="text-xs font-bold text-[#d6006e] hover:underline">
              Open Smith traces →
            </Link>
            <span className="text-xs text-[#c084a0]">Updated {formatDateTime(data.generatedAt)} · auto-refresh 15s</span>
            <button type="button" className={BTN_SOFT} style={BTN_SOFT_STYLE} onClick={() => void reload()}>
              Refresh
            </button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="Overall AI accuracy"
          value={formatPct(accuracy.overallPct)}
          tone={pctTone(accuracy.overallPct)}
          hint={`${accuracy.docsAgreed + accuracy.ratingsAccepted} of ${accuracy.docsReviewed + accuracy.ratingsJudged} analyst decisions matched the AI`}
        />
        <StatCard
          label="Document verification"
          value={formatPct(accuracy.documentPct)}
          tone={pctTone(accuracy.documentPct)}
          hint={`${accuracy.docsAgreed}/${accuracy.docsReviewed} verdicts confirmed by the analyst`}
        />
        <StatCard
          label="Ratings accepted"
          value={formatPct(accuracy.ratingPct)}
          tone={pctTone(accuracy.ratingPct)}
          hint={`${accuracy.ratingsAccepted}/${accuracy.ratingsJudged} published without changing the AI grade (±${data.ratingScoreTolerance} pts)`}
        />
        <StatCard
          label="AI cost (pipeline)"
          value={formatUsd(totals.costUsd)}
          hint={`${formatUsd(totals.avgCostPerCompanyUsd)} avg per company · ${formatTokens(totals.tokens)} tokens · ${totals.calls} calls`}
        />
        <StatCard
          label="Failures"
          value={failureCount}
          tone={failureCount ? 'rose' : 'green'}
          hint={`${failures.aiErrors.length} failed AI calls · ${failures.stuckDocs.length} stuck · ${failures.rejectedDocs.length} docs rejected by AI`}
        />
      </div>

      <Card
        title="Prompt versions"
        subtitle="Immutable snapshots that produced each rating, verification and copilot reply."
        action={
          <Link to="/prompts" className="text-xs font-bold text-[#d6006e] hover:underline">
            Open prompt ledger →
          </Link>
        }
      >
        <p className="text-sm leading-relaxed text-[#7a1f4a]">
          Support now attributes every call to a prompt family and version. Compare live vs canary vs archived on fail rate, analyst acceptance and the actual model output.
        </p>
      </Card>

      <Card title="Ratings by AI" subtitle={`Bands use the AI's own score: Bad < ${data.thresholds.AVERAGE}, Average ${data.thresholds.AVERAGE}–${data.thresholds.GOOD - 1}, Good ≥ ${data.thresholds.GOOD}.`}>
        <div className="grid gap-4 md:grid-cols-3">
          {BAND_ORDER.map((band) => (
            <BandColumn key={band} band={band} companies={data.bands[band]} />
          ))}
        </div>
      </Card>

      <CompanyTable companies={data.companies} />

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <FailurePanel failures={failures} />
        <div className="space-y-6">
          <ConfusionCard confusion={accuracy.confusion} />
          <FeatureCard data={data} />
        </div>
      </div>
    </div>
  );
}

function BandColumn({ band, companies }: { band: Band; companies: CompanyRow[] }) {
  const meta = BAND[band];
  return (
    <div className="rounded-2xl border border-[#fce7f3] bg-[#fffafc] p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusPill label={meta.label} tone={meta.tone} />
          <span className="text-xs text-[#c084a0]">{meta.hint}</span>
        </div>
        <span className="text-sm font-black text-[#6b0030]">{companies.length}</span>
      </div>
      {companies.length === 0 && <p className="py-6 text-center text-xs text-[#c084a0]">No companies in this band.</p>}
      <div className="space-y-2">
        {companies.map((c) => (
          <Link key={c.id} to={`/companies/${c.id}`} className="mz-card-link block rounded-xl border border-[#fce7f3] bg-white p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <CompanyLogo companyId={c.id} name={companyDisplayName(c)} hasLogo={c.hasLogo} className="h-9 w-9" />
                <div className="min-w-0">
                <p className="truncate text-sm font-bold text-[#6b0030]">
                  {flagEmoji(c.countryCode)} {companyDisplayName(c)}
                </p>
                <p className="mt-0.5 text-xs text-[#c084a0]">{COMPANY_STATUS[c.status]?.label || c.status}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-black leading-none" style={{ color: band === 'GOOD' ? '#047857' : band === 'AVERAGE' ? '#b45309' : '#be123c' }}>
                  {c.rating!.aiGrade}
                </p>
                <p className="text-[11px] font-semibold text-[#9d0050]">{c.rating!.aiScore}/100</p>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-[#b0708f]">
              {c.rating!.analystEdited ? (
                <StatusPill label={`Analyst published ${c.rating!.grade} · ${c.rating!.score}`} tone="amber" />
              ) : (
                <StatusPill label="Published as drafted" tone="green" />
              )}
              <span>· {formatUsd(c.ai.costUsd)} AI cost</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function CompanyTable({ companies }: { companies: CompanyRow[] }) {
  if (!companies.length) return <EmptyState title="No companies yet" body="AI metrics appear as soon as customers upload documents." />;
  const sum = (pick: (c: CompanyRow) => number) => companies.reduce((a, c) => a + pick(c), 0);
  return (
    <Card title="Cost & accuracy per company" subtitle="Pipeline cost = document verification + rating + analyst copilot. Accuracy compares each AI verdict with the analyst's final decision.">
      <div className="-mx-5 overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="border-b border-[#fce7f3]">
            <tr>
              <th className={TH}>Company</th>
              <th className={TH}>AI rating</th>
              <th className={TH}>Documents</th>
              <th className={TH}>Doc accuracy</th>
              <th className={TH}>Avg conf.</th>
              <th className={TH}>AI calls</th>
              <th className={`${TH} text-right`}>Verification</th>
              <th className={`${TH} text-right`}>Rating</th>
              <th className={`${TH} text-right`}>Copilot</th>
              <th className={`${TH} text-right`}>Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#fdf2f8]">
            {companies.map((c) => (
              <tr key={c.id} className="hover:bg-[#fffafc]">
                <td className={TD}>
                  <Link to={`/companies/${c.id}`} className="flex items-center gap-2 font-bold text-[#6b0030] hover:text-[#d6006e]">
                    <CompanyLogo companyId={c.id} name={companyDisplayName(c)} hasLogo={c.hasLogo} className="h-8 w-8" />
                    <span className="min-w-0 truncate">{flagEmoji(c.countryCode)} {companyDisplayName(c)}</span>
                  </Link>
                  <div className="mt-1">
                    <StatusPill label={COMPANY_STATUS[c.status]?.label || c.status} tone={COMPANY_STATUS[c.status]?.tone || 'slate'} />
                  </div>
                </td>
                <td className={TD}>
                  {c.rating ? (
                    <div>
                      <span className="font-black text-[#6b0030]">{c.rating.aiGrade}</span> <span className="text-xs text-[#b0708f]">{c.rating.aiScore}/100</span>
                      <div className="mt-1">{c.rating.aiBand && <StatusPill label={BAND[c.rating.aiBand].label} tone={BAND[c.rating.aiBand].tone} />}</div>
                    </div>
                  ) : (
                    <span className="text-xs text-[#c084a0]">Not rated</span>
                  )}
                </td>
                <td className={`${TD} text-xs text-[#6b0030]`}>
                  <div>{c.documents.total} current</div>
                  <div className="text-[#047857]">{c.documents.verified} verified</div>
                  {c.documents.needsReview > 0 && <div className="text-[#b45309]">{c.documents.needsReview} needs review</div>}
                  {c.documents.rejected > 0 && <div className="text-[#be123c]">{c.documents.rejected} rejected</div>}
                  {c.documents.verifying > 0 && <div className="text-[#1d4ed8]">{c.documents.verifying} verifying…</div>}
                  {c.documents.notAiReadable > 0 && <div className="text-[#475569]">{c.documents.notAiReadable} not AI-readable</div>}
                </td>
                <td className={TD}>
                  <StatusPill label={formatPct(c.accuracy.documentPct)} tone={pctTone(c.accuracy.documentPct)} />
                  <div className="mt-1 text-[11px] text-[#b0708f]">
                    {c.accuracy.docsAgreed}/{c.accuracy.docsReviewed} matched
                  </div>
                </td>
                <td className={`${TD} text-xs text-[#6b0030]`}>{c.avgConfidence != null ? `${c.avgConfidence}%` : '—'}</td>
                <td className={`${TD} text-xs text-[#6b0030]`}>
                  {c.ai.calls}
                  {c.ai.failed > 0 && <span className="ml-1 font-bold text-[#be123c]">({c.ai.failed} failed)</span>}
                  <div className="text-[11px] text-[#b0708f]">{formatTokens(c.ai.tokens)} tokens</div>
                </td>
                <td className={`${TD} text-right text-xs`}>{formatUsd(c.ai.verificationUsd)}</td>
                <td className={`${TD} text-right text-xs`}>{formatUsd(c.ai.ratingUsd)}</td>
                <td className={`${TD} text-right text-xs`}>{formatUsd(c.ai.copilotUsd)}</td>
                <td className={`${TD} text-right font-black text-[#6b0030]`}>{formatUsd(c.ai.costUsd)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-[#fce7f3] font-bold text-[#6b0030]">
            <tr>
              <td className={TD} colSpan={5}>
                Total · {companies.length} companies
              </td>
              <td className={`${TD} text-xs`}>{sum((c) => c.ai.calls)}</td>
              <td className={`${TD} text-right text-xs`}>{formatUsd(sum((c) => c.ai.verificationUsd))}</td>
              <td className={`${TD} text-right text-xs`}>{formatUsd(sum((c) => c.ai.ratingUsd))}</td>
              <td className={`${TD} text-right text-xs`}>{formatUsd(sum((c) => c.ai.copilotUsd))}</td>
              <td className={`${TD} text-right`}>{formatUsd(sum((c) => c.ai.costUsd))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

type FailureTab = 'errors' | 'rejected' | 'unreadable' | 'stuck';

function FailurePanel({ failures }: { failures: Overview['failures'] }) {
  const [tab, setTab] = useState<FailureTab>('errors');
  return (
    <Card title="Failures & exceptions" subtitle="What went wrong in the AI pipeline and which documents the AI could not accept.">
      <div className="mb-4">
        <Tabs<FailureTab>
          value={tab}
          onChange={setTab}
          options={[
            { id: 'errors', label: 'AI call failures', count: failures.aiErrors.length },
            { id: 'rejected', label: 'Rejected by AI', count: failures.rejectedDocs.length },
            { id: 'unreadable', label: 'Not AI-readable', count: failures.unreadableDocs.length },
            { id: 'stuck', label: 'Stuck verifying', count: failures.stuckDocs.length },
          ]}
        />
      </div>

      {tab === 'errors' &&
        (failures.aiErrors.length ? (
          <ul className="space-y-2">
            {failures.aiErrors.map((e) => (
              <li key={e.id} className="rounded-xl border border-[#fecdd3] bg-[#fff1f2] p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-bold text-[#be123c]">{FEATURE[e.feature] || e.feature}</span>
                  <span className="text-xs text-[#9f1239]">{formatDateTime(e.createdAt)}</span>
                </div>
                <p className="mt-1 text-xs text-[#6b0030]">
                  {e.company ? <Link to={`/companies/${e.company.id}`} className="font-semibold hover:underline">{companyDisplayName(e.company)}</Link> : 'No company'}
                  {e.document?.title ? ` · ${e.document.title}` : ''} · {e.model}
                </p>
                <p className="mt-1 break-words font-mono text-[11px] text-[#9f1239]">{e.error}</p>
              </li>
            ))}
          </ul>
        ) : (
          <Notice tone="green">No failed AI calls. Every verification, rating and copilot request completed.</Notice>
        ))}

      {tab === 'rejected' &&
        (failures.rejectedDocs.length ? (
          <ul className="space-y-2">
            {failures.rejectedDocs.map((d) => (
              <li key={d.id} className="rounded-xl border border-[#fce7f3] p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link to={`/companies/${d.companyId}`} className="font-bold text-[#6b0030] hover:text-[#d6006e]">
                    {d.companyName} · {d.title} <span className="font-normal text-[#c084a0]">v{d.version}</span>
                  </Link>
                  <StatusPill label={`Now: ${VERDICT[d.finalStatus]?.label || d.finalStatus}`} tone={VERDICT[d.finalStatus]?.tone || 'slate'} />
                </div>
                {d.aiIssues?.length ? (
                  <ul className="mt-1 list-disc pl-5 text-xs text-[#9d0050]">
                    {d.aiIssues.map((i, n) => (
                      <li key={n}>{i}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-[#9d0050]">{d.aiSummary}</p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Notice tone="green">The AI has not rejected any document.</Notice>
        ))}

      {tab === 'unreadable' &&
        (failures.unreadableDocs.length ? (
          <ul className="space-y-2">
            {failures.unreadableDocs.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#e2e8f0] p-3 text-sm">
                <Link to={`/companies/${d.companyId}`} className="font-semibold text-[#6b0030] hover:text-[#d6006e]">
                  {d.companyName} · {d.title} <span className="text-xs font-normal text-[#c084a0]">{d.fileName}</span>
                </Link>
                <StatusPill label={`Manual: ${VERDICT[d.finalStatus]?.label || d.finalStatus}`} tone={VERDICT[d.finalStatus]?.tone || 'slate'} />
              </li>
            ))}
          </ul>
        ) : (
          <Notice tone="green">Every uploaded file was in a format the AI can read.</Notice>
        ))}

      {tab === 'stuck' &&
        (failures.stuckDocs.length ? (
          <ul className="space-y-2">
            {failures.stuckDocs.map((d) => (
              <li key={d.id} className="rounded-xl border border-[#fde68a] bg-[#fffbeb] p-3 text-sm text-[#92400e]">
                {d.companyName} · {d.title} — verifying since {formatDateTime(d.since)}
              </li>
            ))}
          </ul>
        ) : (
          <Notice tone="green">No document has been stuck in AI verification for more than 5 minutes.</Notice>
        ))}
    </Card>
  );
}

const VERDICTS = ['VERIFIED', 'NEEDS_REVIEW', 'REJECTED'];

function ConfusionCard({ confusion }: { confusion: Overview['accuracy']['confusion'] }) {
  return (
    <Card title="AI verdict vs analyst decision" subtitle="Rows = what the AI said, columns = what the analyst decided. The diagonal is agreement.">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="p-1.5 text-left text-[10px] font-bold uppercase text-[#c084a0]">AI ↓ / Analyst →</th>
            {VERDICTS.map((v) => (
              <th key={v} className="p-1.5 text-center text-[10px] font-bold uppercase text-[#c084a0]">
                {VERDICT[v].label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {VERDICTS.map((ai) => (
            <tr key={ai}>
              <td className="p-1.5 font-semibold text-[#6b0030]">{VERDICT[ai].label}</td>
              {VERDICTS.map((analyst) => {
                const n = confusion[ai]?.[analyst] || 0;
                const diagonal = ai === analyst;
                return (
                  <td key={analyst} className="p-1">
                    <div
                      className="rounded-lg py-2 text-center text-sm font-black"
                      style={{ background: n ? (diagonal ? '#ecfdf5' : '#fff1f2') : '#f8fafc', color: n ? (diagonal ? '#047857' : '#be123c') : '#cbd5e1' }}
                    >
                      {n}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function FeatureCard({ data }: { data: Overview }) {
  const pipeline = data.byFeature.filter((f) => f.feature !== 'DEMO_DOC_GENERATION');
  return (
    <Card title="Cost by AI feature">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] font-bold uppercase text-[#c084a0]">
            <th className="py-1 text-left">Feature</th>
            <th className="py-1 text-right">Calls</th>
            <th className="py-1 text-right">Avg latency</th>
            <th className="py-1 text-right">Tokens</th>
            <th className="py-1 text-right">Cost</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#fdf2f8] text-[#6b0030]">
          {pipeline.map((f) => (
            <tr key={f.feature}>
              <td className="py-1.5 font-semibold">{FEATURE[f.feature] || f.feature}</td>
              <td className="py-1.5 text-right">
                {f.calls}
                {f.failed > 0 && <span className="ml-1 text-[#be123c]">({f.failed}✗)</span>}
              </td>
              <td className="py-1.5 text-right">{(f.avgLatencyMs / 1000).toFixed(1)}s</td>
              <td className="py-1.5 text-right">{formatTokens(f.tokens)}</td>
              <td className="py-1.5 text-right font-bold">{formatUsd(f.costUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.totals.demoGenerationCalls > 0 && (
        <p className="mt-3 rounded-lg bg-[#f8fafc] px-2.5 py-2 text-[11px] text-[#64748b]">
          Not included above: {data.totals.demoGenerationCalls} AI calls ({formatUsd(data.totals.demoGenerationUsd)}) used to write the synthetic demo documents.
        </p>
      )}
      <p className="mt-2 text-[11px] text-[#b0708f]">Estimated from token counts at list prices per model family.</p>
    </Card>
  );
}
